import { useMemo } from "react";
import { AxiosError } from "axios";
import { useMutation } from "react-query";

import activities from "../../../redux/activities";
import {
  CreateFieldEnrollmentRequest,
  FieldEnrollmentResponse,
} from "../../../models/enrollments";
import { MutationEnvelope, ensureMutationState } from "../../../services/query";
import enrollmentsService from "../../../services/enrollments";

import { useInvalidateFieldEnrollments } from "./fieldEnrollments.invalidate";
import { useSetFieldEnrollmentsGlobalActivityAndStatus } from "./fieldEnrollments.globalStatus";
import { useCurrentOperationId } from "../operations";
import { useMaybeFabricFeatureFlag } from "../featureFlags";

// #####################
// ## useEnrollFields ##
// #####################

export type ResultOfUseEnrollFields = MutationEnvelope<
  CreateFieldEnrollmentRequest,
  FieldEnrollmentResponse[]
>;
export type UseEnrollFields = () => ResultOfUseEnrollFields;

/**
 * Use the `mutate` function returned from this hook to
 * enroll the given fields in the given feature.
 * This will trigger an invalidation of the field enrollments data.
 *
 * The `data`, `error`, and `status` properties returned from this hook will reflect
 * the state of the mutation.
 */
export const useEnrollFields: UseEnrollFields = () => {
  const currentOperationId = useCurrentOperationId();
  const invalidateFieldEnrollments = useInvalidateFieldEnrollments();
  const catalogFlag = Boolean(
    useMaybeFabricFeatureFlag({
      key: "enrollments-api",
    }),
  );

  const setFieldEnrollmentsGlobalActivityAndStatus =
    useSetFieldEnrollmentsGlobalActivityAndStatus();

  const { data, error, status, mutateAsync } = useMutation<
    FieldEnrollmentResponse[],
    AxiosError,
    CreateFieldEnrollmentRequest
  >(
    (payload) =>
      enrollmentsService.enrollFields(currentOperationId, payload, catalogFlag),
    {
      onMutate: () => {
        setFieldEnrollmentsGlobalActivityAndStatus({
          status: "loading",
          activity: activities.enrollFields,
        });
      },
      onSuccess: async () => {
        await invalidateFieldEnrollments();

        setFieldEnrollmentsGlobalActivityAndStatus({
          status: "success",
          activity: activities.enrollFields,
        });
      },
      onError: () => {
        setFieldEnrollmentsGlobalActivityAndStatus({
          status: "error",
          activity: activities.enrollFields,
        });
      },
    },
  );

  const mutate = mutateAsync;

  return useMemo(
    () => ({
      ...ensureMutationState({ data, error, status }),
      mutate,
    }),
    [data, error, status, mutate],
  );
};
